import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

export default function TelegramModal({ open, onClose }) {
  const [config, setConfig] = useState(null);
  const [botToken, setBotToken] = useState("");
  const [chats, setChats] = useState(null); // null = not detected yet
  const [botUsername, setBotUsername] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get("/telegram/config");
      setConfig(res.data);
    } catch (e) {
      console.error("telegram config error", e);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchConfig();
      setError(null);
      setSuccess(null);
      setChats(null);
      setSelectedChat(null);
    }
  }, [open, fetchConfig]);

  if (!open) return null;

  const detectChats = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    setSuccess(null);
    setChats(null);
    setSelectedChat(null);
    setLoading(true);
    try {
      const res = await api.post("/telegram/detect", { bot_token: botToken.trim() });
      setBotUsername(res.data.bot_username || "");
      const found = res.data.chats || [];
      setChats(found);
      if (found.length === 1) setSelectedChat(found[0].chat_id);
      if (found.length === 0) {
        setError(
          `التوكن صحيح ✅ لكن لم أجد أي محادثة — افتح تيليجرام، أرسل /start للبوت @${res.data.bot_username} ثم اضغط \"كشف المحادثات\" مجدداً`
        );
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "تعذر الكشف — تحقق من التوكن");
    }
    setLoading(false);
  };

  const confirmLink = async () => {
    if (!selectedChat) return;
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const res = await api.post("/telegram/config", { bot_token: botToken.trim(), chat_id: selectedChat });
      setSuccess(`تم الربط بنجاح مع @${res.data.bot_username} — وصلتك رسالة ترحيب على تيليجرام`);
      setBotToken("");
      setChats(null);
      setSelectedChat(null);
      fetchConfig();
    } catch (err) {
      setError(err?.response?.data?.detail || "تعذر الربط");
    }
    setLoading(false);
  };

  const sendTest = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      await api.post("/telegram/test");
      setSuccess("أُرسلت رسالة الاختبار — افحص تيليجرام");
    } catch (err) {
      setError(err?.response?.data?.detail || "فشل الإرسال");
    }
    setLoading(false);
  };

  const sendReport = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      await api.post("/telegram/report");
      setSuccess("أُرسل التقرير — افحص تيليجرام");
    } catch (err) {
      setError(err?.response?.data?.detail || "فشل الإرسال");
    }
    setLoading(false);
  };

  const disable = async () => {
    setLoading(true);
    try {
      await api.post("/telegram/disable");
      setSuccess("تم إيقاف التنبيهات");
      fetchConfig();
    } catch (err) {
      setError("تعذر الإيقاف");
    }
    setLoading(false);
  };

  const chatTypeLabel = (t) =>
    t === "private" ? "محادثة خاصة" : t === "group" || t === "supergroup" ? "مجموعة" : t === "channel" ? "قناة" : t;

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="telegram-modal">
      <div className="modal-box" onClick={(e) => e.stopPropagation()} dir="rtl">
        <div className="modal-head">
          <h3>🔔 تنبيهات تيليجرام</h3>
          <button className="modal-close" onClick={onClose} data-testid="telegram-close-btn">✕</button>
        </div>

        {config?.configured && config?.enabled ? (
          <div className="tg-status ok" data-testid="telegram-status-connected">
            ✔ مرتبط بالبوت <b dir="ltr">@{config.bot_username}</b> — التنبيهات فعّالة
          </div>
        ) : (
          <div className="tg-status" data-testid="telegram-status-disconnected">
            التنبيهات غير مفعّلة بعد
          </div>
        )}

        <div className="tg-steps">
          <p className="modal-sub">الربط بخطوتين فقط — الصق التوكن واختر محادثتك:</p>
          <ol>
            <li>أنشئ بوتاً عبر <b dir="ltr">@BotFather</b> وانسخ الـ Token — ثم <b>أرسل /start لبوتك</b></li>
            <li>الصق التوكن هنا واضغط "كشف المحادثات" ثم اختر محادثتك من القائمة</li>
          </ol>
        </div>

        <form onSubmit={detectChats} className="login-form">
          <label className="field-label">Bot Token</label>
          <input
            className="field-input"
            dir="ltr"
            value={botToken}
            onChange={(e) => setBotToken(e.target.value)}
            placeholder="123456789:AAE..."
            required
            data-testid="telegram-token-input"
          />

          <button
            className="btn btn-primary login-btn"
            type="submit"
            disabled={loading || !botToken.trim()}
            data-testid="telegram-detect-chats-button"
          >
            {loading ? "جاري..." : "🔎 كشف المحادثات"}
          </button>
        </form>

        {chats && chats.length > 0 && (
          <div className="tg-chats-section">
            <p className="modal-sub">
              وجدت <b>{chats.length}</b> محادثة للبوت <b dir="ltr">@{botUsername}</b> — اختر وجهة التنبيهات:
            </p>
            <div className="tg-chat-list" data-testid="telegram-chat-list">
              {chats.map((c) => (
                <button
                  key={c.chat_id}
                  type="button"
                  className={`tg-chat-item ${selectedChat === c.chat_id ? "selected" : ""}`}
                  onClick={() => setSelectedChat(c.chat_id)}
                  data-testid={`telegram-chat-item-${c.chat_id}`}
                >
                  <span className="tg-chat-name">
                    {c.type === "private" ? "👤" : c.type === "channel" ? "📢" : "👥"} {c.name}
                  </span>
                  <span className="tg-chat-meta" dir="ltr">
                    {chatTypeLabel(c.type)} • {c.chat_id}
                  </span>
                </button>
              ))}
            </div>
            <button
              className="btn btn-primary login-btn"
              onClick={confirmLink}
              disabled={loading || !selectedChat}
              data-testid="telegram-confirm-link-button"
            >
              {loading ? "جاري..." : "🔗 ربط التنبيهات بهذه المحادثة"}
            </button>
          </div>
        )}

        {error && <div className="login-error" data-testid="telegram-error">✖ {error}</div>}
        {success && <div className="settings-success" data-testid="telegram-success">✔ {success}</div>}

        {config?.configured && (
          <div className="actions" style={{ marginTop: 14 }}>
            <button className="btn btn-ghost btn-sm" onClick={sendTest} disabled={loading} data-testid="telegram-test-btn">
              📨 إرسال رسالة اختبار
            </button>
            <button className="btn btn-ghost btn-sm" onClick={sendReport} disabled={loading} data-testid="telegram-report-btn">
              📊 إرسال التقرير الآن
            </button>
            {config?.enabled && (
              <button className="btn btn-ghost btn-sm" onClick={disable} disabled={loading} data-testid="telegram-disable-btn">
                🔕 إيقاف التنبيهات
              </button>
            )}
          </div>
        )}

        <p className="modal-sub" style={{ marginTop: 12 }}>
          📅 تقرير أسبوعي تلقائي: نسبة التوفر ومتوسط الاستجابة يُرسلان كل 7 أيام
        </p>
      </div>
    </div>
  );
}
